import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errorText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LANGS = ["English", "French", "German", "Spanish", "Italian", "Portuguese", "Dutch", "Arabic", "Chinese (Simplified)", "Japanese", "Turkish", "Hindi"];

export default function TranslateDialog({ text, onClose }) {
  const { user } = useAuth();
  const [target, setTarget] = useState(() => localStorage.getItem("bpj_lang") || "French");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const allowed = Boolean(user?.entitled);

  useEffect(() => {
    if (!text || !allowed) return undefined;
    let alive = true;
    setBusy(true); setErr(""); setRes(null);
    localStorage.setItem("bpj_lang", target);
    api.post("/translate", { text, target })
      .then((r) => { if (alive) setRes(r.data); })
      .catch((e) => { if (alive) setErr(errorText(e)); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [text, target, allowed]);

  return (
    <Dialog open={Boolean(text)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[var(--paper)] max-w-lg z-[70]" data-testid="translate-dialog">
        <DialogHeader><DialogTitle className="f-serif text-3xl font-normal">Translate</DialogTitle></DialogHeader>
        <p className="f-serif italic text-lg text-[var(--ink-2)] border-l-2 border-[var(--red)] pl-4 max-h-32 overflow-y-auto" data-testid="translate-source">{text}</p>
        {!allowed ? (
          <div className="mt-2" data-testid="translate-locked">
            <p className="text-[var(--ink-2)]">Translation is included with every BPJ subscription.</p>
            <div className="flex gap-3 mt-4">
              <Link to="/subscribe" onClick={onClose} className="btn-red h-10 px-5 inline-flex items-center text-sm font-semibold">See plans</Link>
              {!user && <Link to="/account" onClick={onClose} className="btn-line h-10 px-5 inline-flex items-center text-sm font-semibold">Sign in</Link>}
            </div>
          </div>
        ) : (
          <>
            <label className="flex items-center gap-3 text-sm"><span className="eyebrow">Into</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="border rule bg-white h-9 px-2" data-testid="translate-lang-select">{LANGS.map((l) => <option key={l}>{l}</option>)}</select>
            </label>
            <div className="min-h-[80px] bg-white border rule p-4" data-testid="translate-result">
              {busy && <p className="flex items-center gap-2 text-[var(--muted)]"><Loader2 size={16} className="animate-spin" /> Translating…</p>}
              {err && <p className="text-sm text-red-700">{err}</p>}
              {res && <>
                <p className="f-serif text-2xl text-[var(--ink)]">{res.translation}</p>
                {res.definition && <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{res.definition}</p>}
              </>}
            </div>
            <p className="text-xs text-[var(--muted)]">AI translation — may contain mistakes.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
